<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        :excelHref="excelHref"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <go-back-icon-button @click="goBack" v-if="isTableView" />
        </template>
        <template v-slot:card-title>
            {{ title }}
        </template>

        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <div class="h-100">
                <vue-apex-charts
                    v-if="ready && series.length > 0 && !isTableView"
                    id="staylio-install-base-bar-chart"
                    class="staylio-install-base-bar-chart"
                    ref="staylioInstallBaseBarChart"
                    height="350"
                    :series="series"
                    :options="options"
                />
                <staylio-install-base-card-grid
                    v-if="isTableView"
                    :getDataParams="getDataParams"
                    :getDataEndpoint="getDataEndpoint"
                    :cardGridKey="cardGridKey"
                    :limit="limit"
                    :tabWidget="tabWidget"
                />
            </div>
        </template>

        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import ActionsCellRenderer from '../common/reservations-actions-cell-renderer.vue';
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGrid from '../../../generic/card-grid/card-grid.vue';
import CardGridItem from '../common/card-grid-item.vue';
import {
    DASHBOARD_STAYLIO_INSTALL_BASE_ENDPOINT,
    DASHBOARD_DASHBOARD_LIST_ENDPOINT,
    DASHBOARD_STAYLIO_INSTALL_BASE_GRID_ENDPOINT,
} from '../../../../endpoints';
import { getWidgetGridRowsNumber } from '../helpers';
import { mapGetters } from 'vuex';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import ReservationCardGrid from '../common/reservations-card-grid.vue';
import ExportToExcelButton from '../../../generic/export-to-excel-button/export-to-excel-button.vue';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import StaylioInstallBaseCardGrid from '../common/staylio-install-base-card-grid.vue';

export default {
    name: 'StaylioInstallBaseChartCardGridItem',
    mixins: [cardGridItemBaseMixin],
    components: {
        BusyModal,
        VueApexCharts,
        CardGrid,
        CardGridItem,
        ResponsiveButton,
        TabWidgetSettingsModalForm,
        ReservationCardGrid,
        ExportToExcelButton,
        actionsCellRenderer: ActionsCellRenderer,
        GoBackIconButton,
        StaylioInstallBaseCardGrid,
    },
    data() {
        return {
            busy: null,
            series: [],
            hasData: false,
            selectedChartPart: null,
            isTableView: false,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
            labels: [],
            booking_method_totals: [],
            data: [],
            selectedBookingMethod: null,
            defaultName: 'Staylio Userbase',
        };
    },
    mounted() {
        this.fetchChartData();
    },
    computed: {
        ...mapGetters('header', ['userPermissions']),
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                },
            };
        },
        title() {
            return this.tabWidget.settings.name || this.defaultName;
        },
        excelHref() {
            return `${DASHBOARD_DASHBOARD_LIST_ENDPOINT() + 'staylio-install-base-excel/'}`;
        },
        options() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            const bookingMethod = this.data.find(
                                (item, index) => index === config.dataPointIndex
                            )?.booking_method_case;
                            this.selectedBookingMethod = bookingMethod;

                            setTimeout(() => {
                                this.isTableView = true;
                                this.handleResized();
                            }, 100);
                        },
                    },
                },
                yaxis: {
                    max: 100,
                    labels: {
                        formatter: (val) => `${val}%`,
                    },
                },
                plotOptions: {
                    bar: {
                        distributed: true,
                    },
                },
                annotations: {
                    points: this.data.map((item) => {
                        let yPosition = ((item.installed_staylio / item.total) * 100).toFixed(2);
                        if (!(yPosition > 0)) {
                            yPosition = 0;
                        }
                        return {
                            x: item.booking_method_case,
                            y: yPosition,
                            marker: {
                                size: 0,
                                strokeWidth: 0,
                            },
                            label: {
                                borderColor: 'transparent',
                                style: {
                                    color: '#000',
                                    background: 'transparent',
                                },
                                text: `${item.installed_staylio} / ${item.total}`,
                            },
                        };
                    }),
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val, { dataPointIndex, w }) => {
                        const total = this.data[dataPointIndex].total;
                        if (val === 0 || total === 0) {
                            return ``;
                        } else {
                            return `${val}%`;
                        }
                    },
                    style: {
                        fontSize: '10px',
                    },
                },
                xaxis: {
                    categories: this.labels,
                },
                tooltip: {
                    custom: (params) => {
                        const { series, seriesIndex, dataPointIndex, w } = params;
                        const total = this.data[dataPointIndex].total;
                        const count = this.data[dataPointIndex].installed_staylio;
                        const percentage = series[seriesIndex][dataPointIndex] || 0;

                        const content = `${count} / ${total} (${percentage}%) ${this.data[dataPointIndex].booking_method_case} Guests w/ Staylio Installed`;

                        return (
                            '<div class="staylio-install-base-widget-tooltip">' +
                            '<span>' +
                            content +
                            '</span>' +
                            '</div>'
                        );
                    },
                },
            };
        },
        getDataParams() {
            return {
                booking_method: this.selectedBookingMethod === 'Total' ? null : this.selectedBookingMethod,
            };
        },
        getDataEndpoint() {
            return DASHBOARD_STAYLIO_INSTALL_BASE_GRID_ENDPOINT();
        },
    },
    methods: {
        goBack() {
            this.isTableView = false;
        },
        async fetchChartData() {
            try {
                this.busy = true;
                const responseData = await API.get(DASHBOARD_STAYLIO_INSTALL_BASE_ENDPOINT());
                this.data = responseData.data;

                const tmpData = [];

                this.data.forEach((item) => {
                    let percentage = ((item.installed_staylio / item.total) * 100).toFixed(2);
                    item.booking_method_case = item.booking_method_case;
                    this.labels.push(item.booking_method_case);
                    if (!percentage || percentage === 'NaN') {
                        percentage = '0.00';
                    }
                    tmpData.push(percentage);
                });
                
                this.series = [{ data: tmpData }];
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            });
            this.cardGridKey += 1;
        },
    },
};
</script>

<style lang="scss">
.staylio-install-base-bar-chart {
    .svg_select_points {
        display: none !important;
    }
    .monthly-manual-select {
        max-height: 40px;
        fieldset {
            margin-bottom: 0;
        }
    }
}
.staylio-install-base-widget-tooltip {
    padding: 4px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
    color: #373d3f;
    > span {
        padding: 0;
    }
}
</style>
